import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import styles from './DialogCloseButton.module.css';

export interface DialogCloseButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'type'> {
  /** Accessible name. Defaults to `shared.close` via i18n. */
  readonly 'aria-label'?: string;
  readonly testId?: string;
  readonly className?: string;
}

/**
 * Additive Lucide X close control — 20px icon (`--icon-size-md`) on a 44px hit target.
 * Presentation only; pair with `Dialog.Close asChild` or an `onClick` dismiss handler.
 * Matches AppModal / FullScreenDialog close chrome.
 */
export const DialogCloseButton = forwardRef<HTMLButtonElement, DialogCloseButtonProps>(
  (
    {
      'aria-label': ariaLabel,
      testId = 'dialog-close',
      className,
      disabled,
      ...rest
    },
    ref,
  ) => {
    const { t } = useTranslation('admin');
    const label = ariaLabel?.trim() ? ariaLabel : t('shared.close');

    return (
      <button
        {...rest}
        ref={ref}
        type="button"
        className={[styles.closeButton, className].filter(Boolean).join(' ')}
        aria-label={label}
        title={rest.title ?? label}
        data-testid={testId}
        disabled={disabled}
      >
        <X className={styles.closeIcon} aria-hidden="true" />
      </button>
    );
  },
);

DialogCloseButton.displayName = 'DialogCloseButton';
